import React, { useEffect, useState } from 'react';
import { DatePicker, Form, Input, Radio, Select, Space } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import get from 'lodash/get';
import moment from 'moment';

import DetailRow from '../../../components/common/DetailRow';
import Note from '../../../components/common/Note';
import BookingActionBar from '../../../components/NewBooking/BookingActionBar';
import {
  BookingForm,
  FieldRow,
  FormCardBody,
} from '../../../components/NewBooking/BookingForm';
import {
  Card,
  CardBody,
  OptionalMark,
  SectionTitle,
} from '../../../components/common/Card';
import { updateBookingData } from '../../../slices/booking';
import {
  CARD_TYPE,
  CARD_TYPE_LABEL,
  NO_REBATE_CARD_TYPES,
} from '../../../constants/cardType';
import { DVA_CARD_TYPE_OPTIONS } from '../../../constants/dvaCardType';
import { RELATIONSHIPS } from '../../../constants/relationships';
import { TIME_FORMAT } from '../../../constants/time';
import { removeEmptyStrFields } from '../../../utils/bookingUtils';
import { formatTotalFeeDisplay } from '../../../utils/feeUtils';
import { toPhoneDigits } from '../../../utils/phoneUtils';
import { scrollToFirstFormError } from '../../../utils/scrollUtils';
import analytics from '../../../infra/analytics';

const { Option } = Select;

const MEDICARE_IRN_HINT = 'The number beside your name on the card.';
const NO_REBATE_MESSAGE =
  'Please be advised that if you do not possess a Medicare or DVA card, the Medicare rebate will not be applicable to your healthcare services. As a result, a fully private fee will apply to all services provided. We appreciate your understanding and cooperation.';
const UNDER_18_MESSAGE =
  "For patients under 18, please provide parent's or guardian's Medicare card details";
const STORED_DATE_LENGTH = 10;
const EXPIRY_PARSE_FORMATS = [
  TIME_FORMAT.DATE_WITH_DASH,
  TIME_FORMAT.TABLE_MONTH,
];

const buildMedicarePrefill = (storedMedicare, fallbackCardNumber) => {
  const expiryMoment = storedMedicare.medicareCardExpireDate
    ? moment(storedMedicare.medicareCardExpireDate, EXPIRY_PARSE_FORMATS)
    : null;

  return {
    cardType: storedMedicare.cardType || CARD_TYPE.MEDICARE,
    medicareCardNumber:
      storedMedicare.medicareCardNumber || fallbackCardNumber || '',
    medicareCardIRN: storedMedicare.medicareCardIRN || '',
    medicareFirstName: storedMedicare.medicareFirstName || '',
    medicareSurname: storedMedicare.medicareSurname || '',
    expiry: expiryMoment?.isValid() ? expiryMoment : null,
    dvaNumber: storedMedicare.dvaNumber || '',
    dvaCardType: storedMedicare.dvaCardType || undefined,
  };
};

const resolveDateOfBirthMoment = (bookingData, workflowData) => {
  const enteredDateOfBirth = get(
    bookingData,
    'personalDetails.dateOfBirth',
    null
  );

  if (enteredDateOfBirth) {
    const enteredMoment = moment(enteredDateOfBirth);

    return enteredMoment.isValid() ? enteredMoment : null;
  }

  const storedDateOfBirth = get(workflowData, 'client.dateOfBirth', null);

  if (!storedDateOfBirth) return null;

  const storedMoment = moment(
    String(storedDateOfBirth).slice(0, STORED_DATE_LENGTH),
    TIME_FORMAT.DATE_WITH_DASH
  );

  return storedMoment.isValid() ? storedMoment : null;
};

const Medicare = () => {
  const [form] = Form.useForm();
  const { workflowId } = useParams();
  const bookingData = useSelector(({ booking }) => booking.data);
  const workflowData = useSelector(({ workflow }) => workflow.data);
  const dispatch = useDispatch();

  const { medicare } = bookingData;
  const storedMedicare = get(workflowData, 'client.medicare', {}) || {};
  const fallbackCardNumber = get(
    workflowData,
    'specialist.medicareCardNumber',
    ''
  );
  const totalFeeDisplay = formatTotalFeeDisplay(
    get(workflowData, 'specialist.fee', null)
  );

  const [cardType, setCardType] = useState(
    () => medicare?.cardType || storedMedicare.cardType || CARD_TYPE.MEDICARE
  );

  const dateOfBirthMoment = resolveDateOfBirthMoment(bookingData, workflowData);
  const isPatientUnder18 = dateOfBirthMoment
    ? moment().diff(dateOfBirthMoment, 'years') < 18
    : false;
  const hasNoRebate = NO_REBATE_CARD_TYPES.includes(cardType);

  useEffect(() => {
    analytics.logPageViewEvent('fill_medicare_details', {
      workflow_id: workflowId,
    });
  }, [workflowId]);

  useEffect(() => {
    if (!medicare) {
      const prefilledMedicare = buildMedicarePrefill(
        storedMedicare,
        fallbackCardNumber
      );

      form.setFieldsValue({ medicare: prefilledMedicare });
      setCardType(prefilledMedicare.cardType);
      return;
    }

    form.setFieldsValue({
      medicare,
      nextOfKin: bookingData.nextOfKin,
      bookingInfo: bookingData.bookingInfo,
    });
    setCardType(medicare.cardType || CARD_TYPE.MEDICARE);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [medicare, form]);

  useEffect(() => {
    if (medicare || !workflowData.client) return;

    form.setFieldsValue({
      nextOfKin: workflowData.client.nextOfKin,
      bookingInfo: workflowData.client.bookingInfo,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, workflowData.client]);

  const handleCardTypeChange = (event) => {
    const selectedCardType = event.target.value;

    setCardType(selectedCardType);
    form.setFieldsValue({
      medicare: {
        cardType: selectedCardType,
        medicareFirstName: '',
        medicareSurname: '',
        medicareCardNumber: '',
        medicareCardIRN: '',
        expiry: null,
        dvaNumber: '',
        dvaCardType: undefined,
      },
    });
  };

  const handleSubmitForm = async () => {
    try {
      const values = await form.validateFields();
      const { medicare: medicareValues, nextOfKin, bookingInfo } = values;

      dispatch(
        updateBookingData({
          medicare: removeEmptyStrFields({
            ...medicareValues,
            expiry: medicareValues.expiry?.clone().endOf('month'),
          }),
          nextOfKin: removeEmptyStrFields(nextOfKin),
          bookingInfo: removeEmptyStrFields(bookingInfo),
        })
      );

      return true;
    } catch (errorInfo) {
      scrollToFirstFormError(form, errorInfo);
      console.log('Medicare info failed:', errorInfo);
      return false;
    }
  };

  return (
    <>
      <BookingForm layout="vertical" form={form}>
        <Card>
          <FormCardBody>
            <SectionTitle>Medicare</SectionTitle>

            <Form.Item
              name={['medicare', 'cardType']}
              initialValue={CARD_TYPE.MEDICARE}
            >
              <Radio.Group onChange={handleCardTypeChange}>
                <Space direction="vertical">
                  <Radio value={CARD_TYPE.MEDICARE}>
                    {CARD_TYPE_LABEL[CARD_TYPE.MEDICARE]}
                  </Radio>
                  <Radio value={CARD_TYPE.DVA}>
                    {CARD_TYPE_LABEL[CARD_TYPE.DVA]}
                  </Radio>
                  <Radio value={CARD_TYPE.NONE}>
                    {CARD_TYPE_LABEL[CARD_TYPE.NONE]}
                  </Radio>
                  <Radio value={CARD_TYPE.NOT_WITH_ME}>
                    {CARD_TYPE_LABEL[CARD_TYPE.NOT_WITH_ME]}
                  </Radio>
                </Space>
              </Radio.Group>
            </Form.Item>

            {hasNoRebate && <Note tone="warning">{NO_REBATE_MESSAGE}</Note>}

            {cardType === CARD_TYPE.MEDICARE && (
              <>
                {isPatientUnder18 && (
                  <>
                    <Note>{UNDER_18_MESSAGE}</Note>

                    <FieldRow>
                      <Form.Item
                        label="Medicare First Name"
                        name={['medicare', 'medicareFirstName']}
                        rules={[
                          {
                            required: true,
                            message: 'Please input the Medicare First Name.',
                          },
                        ]}
                      >
                        <Input />
                      </Form.Item>

                      <Form.Item
                        label="Medicare Surname"
                        name={['medicare', 'medicareSurname']}
                        rules={[
                          {
                            required: true,
                            message: 'Please input the Medicare Surname.',
                          },
                        ]}
                      >
                        <Input />
                      </Form.Item>
                    </FieldRow>
                  </>
                )}

                <Form.Item
                  label="Medicare Card Number"
                  required
                  name={['medicare', 'medicareCardNumber']}
                  rules={[
                    {
                      required: true,
                      message: 'Please input the Medicare card number.',
                    },
                    {
                      pattern: /^(\d{10})$/,
                      message: 'Please input a valid Medicare card number.',
                    },
                  ]}
                >
                  <Input placeholder="*********" />
                </Form.Item>

                <FieldRow>
                  <Form.Item
                    label="Medicare Card IRN"
                    required
                    extra={MEDICARE_IRN_HINT}
                    name={['medicare', 'medicareCardIRN']}
                    rules={[
                      {
                        required: true,
                        message: 'Please input your Medicare card IRN.',
                      },
                      {
                        pattern: /^(\d)$/,
                        message: 'Please input a valid Medicare card IRN.',
                      },
                    ]}
                  >
                    <Input placeholder="*" />
                  </Form.Item>

                  <Form.Item
                    label="Expiry"
                    required
                    name={['medicare', 'expiry']}
                    rules={[
                      {
                        required: true,
                        message: 'Please select the expiry date.',
                      },
                    ]}
                  >
                    <DatePicker
                      picker="month"
                      placeholder="MM/YYYY"
                      format={TIME_FORMAT.TABLE_MONTH}
                    />
                  </Form.Item>
                </FieldRow>
              </>
            )}

            {cardType === CARD_TYPE.DVA && (
              <FieldRow>
                <Form.Item
                  label="DVA Card Number"
                  required
                  name={['medicare', 'dvaNumber']}
                  rules={[
                    {
                      required: true,
                      message: 'Please input your DVA card number.',
                    },
                    {
                      pattern:
                        /^[N,V,Q,S,W,T]([A-Z ][0-9]{1,6}|[A-Z]{2}[0-9]{1,5}|[A-Z]{3}[0-9]{1,4})[A-Z]?$/,
                      message: 'Please input a valid DVA card number.',
                    },
                  ]}
                >
                  <Input placeholder="Enter DVA card number" />
                </Form.Item>

                <Form.Item
                  label="DVA Card type"
                  required
                  name={['medicare', 'dvaCardType']}
                  rules={[
                    {
                      required: true,
                      message: 'Please select your DVA card type.',
                    },
                  ]}
                >
                  <Select placeholder="Please select DVA card type.">
                    {DVA_CARD_TYPE_OPTIONS.map(({ value, label }) => (
                      <Option value={value} key={value}>
                        {label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </FieldRow>
            )}
          </FormCardBody>
        </Card>

        {hasNoRebate && totalFeeDisplay && (
          <Card>
            <CardBody>
              <SectionTitle>Amount payable</SectionTitle>
              <DetailRow
                label="Total Fee"
                value={totalFeeDisplay}
                currency="AUD"
                emphasised
              />
            </CardBody>
          </Card>
        )}

        <Card>
          <FormCardBody>
            <SectionTitle>
              Next of kin<OptionalMark>(optional)</OptionalMark>
            </SectionTitle>

            <Form.Item label="Name" name={['nextOfKin', 'name']}>
              <Input placeholder="Full name" />
            </Form.Item>

            <FieldRow>
              <Form.Item
                label="Mobile Number"
                name={['nextOfKin', 'phone']}
                rules={[
                  {
                    transform: toPhoneDigits,
                    pattern: /^(61|0)[0-9]{9}$/,
                    message: 'Please input a valid phone number.',
                  },
                ]}
              >
                <Input placeholder="04** *** ***" />
              </Form.Item>

              <Form.Item
                label="Relationship to You"
                name={['nextOfKin', 'relationship']}
              >
                <Select placeholder="Please select relationship">
                  {RELATIONSHIPS.map(({ value, label }) => (
                    <Option value={value} key={value}>
                      {label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </FieldRow>
          </FormCardBody>
        </Card>

        <Card>
          <FormCardBody>
            <SectionTitle>For your clinician</SectionTitle>

            <Form.Item label="Allergies" name={['bookingInfo', 'allergies']}>
              <Input.TextArea
                rows={3}
                placeholder="Please input any allergies you have"
              />
            </Form.Item>

            <Form.Item
              label="Current Medication"
              name={['bookingInfo', 'currentMedication']}
            >
              <Input.TextArea
                rows={3}
                placeholder="Please input any current medications"
              />
            </Form.Item>
          </FormCardBody>
        </Card>

        <Note>
          If you are eligible for a Medicare rebate for this service, we will
          lodge the Medicare patient claim on your behalf within{' '}
          <b>3 business days of you attending your appointment</b>. Your rebate
          may vary based on your eligibility.
        </Note>
      </BookingForm>

      <BookingActionBar onNext={handleSubmitForm} />
    </>
  );
};

export default Medicare;
